import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Check, KeyRound, Mail, Phone, UserPlus, X } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuthStore } from "@/store/auth";
import { canManageContractors } from "@/features/contractors/utils";
import {
  splitPersonName,
  type ContractorFlowState,
} from "@/features/contractors/prefill";
import {
  ACCOUNT_REQUEST_STATUSES,
  useAccountRequests,
  useDecideAccountRequest,
  type AccountRequest,
  type AccountRequestStatus,
} from "@/features/rfq/api/proceedings";

/**
 * Prośby o konto z publicznej strony postępowań.
 *
 * To lista do obdzwonienia, nie rejestr użytkowników: konta zakłada wyłącznie GK, a
 * zgłoszenie zamyka się decyzją, która zostawia ślad kto i kiedy. Bez tego nie
 * wiadomo, czy z firmą ktoś już rozmawiał.
 *
 * „Znamy tę firmę" liczymy po NIP i pokazujemy WYŁĄCZNIE tutaj — publiczny formularz
 * nigdy nie odpowiada, czy dana firma jest naszym podwykonawcą.
 */

const STATUS_COLOR: Record<
  AccountRequestStatus,
  "yellow" | "blue" | "green" | "gray"
> = {
  new: "yellow",
  contacted: "blue",
  account_created: "green",
  rejected: "gray",
};

export function AccountRequestsPage() {
  usePageTitle("Prośby o konto");
  const [status, setStatus] = useState<AccountRequestStatus | "">("");
  const { data, isLoading } = useAccountRequests(status);

  if (isLoading) return <PageLoader />;
  const rows = data ?? [];

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Prośby o konto
      </h1>
      <div className="mt-1 h-0.5 w-8 bg-[var(--color-primary)]" />
      <p className="mt-3 max-w-2xl text-sm text-[var(--color-text-secondary)]">
        Zgłoszenia firm z publicznej strony postępowań. Konto zakłada GK — tutaj
        odnotowujemy, co się z każdym zgłoszeniem stało.
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as AccountRequestStatus | "")}
          className="border border-[var(--color-border)] bg-transparent px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
        >
          <option value="">Wszystkie statusy</option>
          {ACCOUNT_REQUEST_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <span className="text-sm text-[var(--color-text-muted)]">
          {rows.length} zgłoszeń
        </span>
      </div>

      <div className="mt-4 space-y-2">
        {rows.length === 0 ? (
          <p className="border border-dashed border-[var(--color-border)] px-4 py-10 text-center text-sm text-[var(--color-text-muted)]">
            Brak zgłoszeń.
          </p>
        ) : (
          rows.map((row) => <RequestCard key={row.id} request={row} />)
        )}
      </div>
    </div>
  );
}

function RequestCard({ request }: { request: AccountRequest }) {
  const decide = useDecideAccountRequest();
  const navigate = useNavigate();
  const canManage = canManageContractors(useAuthStore((s) => s.user));
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(request.note);

  const act = (status: AccountRequestStatus) =>
    decide.mutate({ id: request.id, status, note });

  // Firma z tego zgłoszenia: najpierw fakt (dodana stąd), potem domysł po NIP.
  const contractor = request.linked_contractor ?? request.known_contractor;

  /** Dane konta — zgadywane z jednego pola „osoba kontaktowa", więc do poprawienia w formularzu. */
  const userPrefill = () => ({
    ...splitPersonName(request.contact_person),
    email: request.email,
    phone_number: request.phone,
    // Firma przyszła z ogłoszenia o postępowaniu — po to prosi o konto.
    can_access_rfq: true,
  });

  /** Kartoteka + konto to jedna droga: formularz firmy, a po zapisie od razu konto. */
  const addToRegistry = () => {
    const state: ContractorFlowState = {
      contractorPrefill: {
        name: request.company_name,
        nip: request.nip,
        contact_person: request.contact_person,
        contact_email: request.email,
        contact_phone: request.phone,
      },
      openUserForm: userPrefill(),
      accountRequestId: request.id,
    };
    navigate("/contractors/new", { state });
  };

  const createAccount = () => {
    if (!contractor) return;
    const state: ContractorFlowState = {
      openUserForm: userPrefill(),
      accountRequestId: request.id,
    };
    navigate(`/contractors/${contractor.id}`, { state });
  };

  return (
    <div className="border border-[var(--color-border)] bg-white p-4 dark:bg-gray-800">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-base font-semibold text-[var(--color-text-primary)]">
            <Building2 size={15} className="text-[var(--color-text-muted)]" />
            {request.company_name}
            {request.nip && (
              <span className="font-mono text-xs text-[var(--color-text-muted)]">
                NIP {request.nip}
              </span>
            )}
          </p>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {request.contact_person}
          </p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-text-secondary)]">
            <a href={`mailto:${request.email}`} className="flex items-center gap-1 hover:underline">
              <Mail size={12} /> {request.email}
            </a>
            {request.phone && (
              <a href={`tel:${request.phone}`} className="flex items-center gap-1 hover:underline">
                <Phone size={12} /> {request.phone}
              </a>
            )}
            <span>{new Date(request.created_at).toLocaleString("pl")}</span>
          </div>
        </div>
        <Badge color={STATUS_COLOR[request.status]}>{request.status_label}</Badge>
      </div>

      {request.message && (
        <p className="mt-2 whitespace-pre-wrap border-l-2 border-[var(--color-border)] pl-2 text-sm text-[var(--color-text-secondary)]">
          {request.message}
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {request.proceeding_number && (
          <span className="text-[var(--color-text-muted)]">
            Z ogłoszenia:{" "}
            <Link
              to={`/rfq/proceedings/${request.proceeding}`}
              className="text-[var(--color-primary)] hover:underline"
            >
              {request.proceeding_number}
            </Link>
          </span>
        )}
        {/* Dopasowanie po NIP — wyłącznie dla nas. */}
        {contractor ? (
          <span className="text-amber-700 dark:text-amber-500">
            {request.linked_contractor
              ? "Firma dodana z tego zgłoszenia: "
              : "Tę firmę już mamy w kartotece: "}
            <Link to={`/contractors/${contractor.id}`} className="underline">
              {contractor.name}
            </Link>
          </span>
        ) : (
          request.nip && (
            <span className="text-[var(--color-text-muted)]">
              Firmy nie ma jeszcze w kartotece.
            </span>
          )
        )}
      </div>

      {request.handled_by_name && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          {request.handled_by_name} ·{" "}
          {request.handled_at && new Date(request.handled_at).toLocaleString("pl")}
          {request.note && ` · ${request.note}`}
        </p>
      )}

      <div className="mt-3 border-t border-[var(--color-border)] pt-3">
        {!open ? (
          <div className="flex flex-wrap gap-2">
            {/* Jedno kliknięcie prowadzi całą drogę: dane firmy do poprawienia,
                a zaraz po zapisie konto — nic nie trzeba przepisywać ręcznie. */}
            {canManage &&
              (contractor ? (
                <Button size="sm" onClick={createAccount}>
                  <KeyRound size={13} className="mr-1 inline" /> Załóż konto
                </Button>
              ) : (
                <Button size="sm" onClick={addToRegistry}>
                  <Building2 size={13} className="mr-1 inline" /> Dodaj
                  podwykonawcę
                </Button>
              ))}
            <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
              Odnotuj decyzję
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Notatka wewnętrzna (np. z kim rozmawiano)"
              className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
            />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => act("account_created")} disabled={decide.isPending}>
                <UserPlus size={13} className="mr-1 inline" /> Konto założone
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => act("contacted")}
                disabled={decide.isPending}
              >
                <Check size={13} className="mr-1 inline" /> Skontaktowano się
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => act("rejected")}
                disabled={decide.isPending}
              >
                <X size={13} className="mr-1 inline" /> Odrzuć
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
                Anuluj
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
